import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  DateRange,
  ErrorResponse,
  extractDateRange,
  extractDefaultStoreId,
  extractNumber,
  extractNumberArr,
  HxReportService,
  HxUiLabelPipe,
  ProductInfoType,
  ProductReleaseReportModel,
  StoreBasicModel,
  toRouterQueryParams
} from 'hx-services';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorHandlerService } from '@manager-app/service/error-handler.service';
import { Subscription } from 'rxjs';
import { saveAs } from 'file-saver';
import { HcToastrService } from '@manager-app/service/toastr.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { HxDateRangeSelectComponent, HxLoaderComponent, HxProductInfoSelectComponent, HxStoreSelectComponent } from 'hx-component';
import { FormsModule } from '@angular/forms';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { NgbPagination } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'app-product-release.m-grid__item.m-grid__item--fluid.m-wrapper',
    templateUrl: './product-release.component.html',
    styleUrls: ['./product-release.component.css'],
    imports: [HxStoreSelectComponent, FormsModule, HxDateRangeSelectComponent, HxProductInfoSelectComponent, NgIf, NgFor, NgClass, HxLoaderComponent, NgbPagination, TranslocoPipe, HxUiLabelPipe]
})
export class ProductReleaseComponent implements OnInit, OnDestroy {
  productReleaseList: ProductReleaseReportModel[] = [];
  params: {
    storeId?: number;
    date?: DateRange,
    productInfoIds?: number[],
    page: number,
    limit: number,
  } = {page: 1, limit: 20};
  pagination = {
    allItemCount: 0,
  };
  isLoading = {
    list: false,
    generate: false,
  };
  brandId?: number;
  selectedPITypes: ProductInfoType[] = [ProductInfoType.PRODUCT, ProductInfoType.PROPERTY];
  private sub: Subscription;

  constructor(
    private reportService: HxReportService,
    private aRoute: ActivatedRoute,
    private router: Router,
    private errorService: ErrorHandlerService,
    private toastr: HcToastrService,
    private tr: TranslocoService
  ) {
  }

  ngOnInit(): void {
    this.sub = this.aRoute.queryParamMap.subscribe(params => {
      this.params.date = extractDateRange('date', params);
      this.params.page = extractNumber('page', params) ?? 1;
      this.params.storeId = extractNumber('storeId', params) ?? extractDefaultStoreId();
      this.params.limit = extractNumber('limit', params) ?? 20;
      this.params.productInfoIds = extractNumberArr('productInfoIds', params);
      if (this.params.storeId) {
        this.loadList();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  reset() {
    this.params = {page: 1, limit: 20};
    this.navigate();
  }

  pageChanged(event: number) {
    this.params.page = event;
    this.navigate();
  }

  generate() {
    this.isLoading.generate = true;
    this.reportService.generateProductRelease(this.params).subscribe(response => {
      this.isLoading.generate = false;
      const contentDispositionHeader = response.headers.get('Content-Disposition');
      const result = contentDispositionHeader.split(';')[1].trim().split('=')[1];
      const blob = new Blob([response.body], {type: 'application/octet-stream'});
      saveAs(blob, result);
      this.loadList();
      this.toastr.success(this.tr.translate('product-release.ts.success'));
    }, err => {
      this.isLoading.generate = false;
      new Response(err.error).json().then(errRes => this.errorService.handler(errRes as ErrorResponse));
    });
  }

  applyFilters() {
    this.params.page = 1;
    this.navigate();
  }

  private navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  private loadList() {
    this.isLoading.list = true;
    this.productReleaseList = [];

    this.reportService.productRelease(this.params).subscribe(response => {
      this.productReleaseList = response.list || [];
      this.pagination.allItemCount = response.count;
      this.isLoading.list = false;
    }, err => {
      this.isLoading.list = false;
      this.errorService.handler(err.error);
    });
  }

  onStoreChanged(store: StoreBasicModel | StoreBasicModel[] | undefined) {
    if (Array.isArray(store)) {
      return;
    }
    if (!store || this.brandId !== store?.brandId) {
      this.params.productInfoIds = [];
    }
    this.brandId = store?.brandId;
    this.applyFilters();
  }
}
